/**
 * CrudTable — shared primitive library for Wave-2/4/6/6b admin catalog editors.
 *
 * History:
 *   - Wave 2 introduced the layout shell + plain-language banner pattern
 *     (CrudPage / CrudHeader / CrudToastBanner / CrudEmptyState).
 *   - Wave 4 added concurrency primitives (buildConcurrencyEnvelope,
 *     detectConcurrencyConflict, emitAdminConflictEvent, CrudConcurrencyBanner)
 *     plus the form helpers (Field, FkSelectField, NumericRangeField,
 *     ValidatedField, JsonValidatedField, AuditRowFooter).
 *   - Wave 6 added mutability gating (CatalogMutability + provider/hook +
 *     CrudAddButton / CrudDeleteButton auto-hide + CatalogMutabilityBanner).
 *   - Wave 6b modernized the table chrome (sticky Th, zebra rows, accent rail
 *     on hover, animated empty-state SVG, CrudSearchInput, CrudResultCount,
 *     CrudToolbar, CrudSkeletonRows, CrudRowActions, CrudEditButton,
 *     CrudDeleteButton, CrudCompactPill) via a single runtime-injected
 *     stylesheet keyed on `data-crud-cell` / `data-crud-head` / `data-crud-action`.
 *
 * Bill 2026-04-24 directive (No more stubs or placeholders): every catalog
 * editor must be self-explanatory, plain-language, and modern. This module
 * extracts the boilerplate that all ~40 catalog editors share so they read as
 * intent-only (column defs + a render-prop modal).
 *
 * Implementation notes
 *   - Pure React 18 + Lucide icons + the existing primitives library
 *     (@/components/primitives). NO CDN, NO new npm deps.
 *   - Inline styles + a single runtime-injected stylesheet for hover/zebra/
 *     shimmer/empty-state. Idempotent via `ensureCrudTableStyles()` and a
 *     module-level flag.
 *   - CSS variable tokens (--ink-XXX, --pcb-blue, --surface-card, --r-md,
 *     --sp-N, --err-text) are defined in apps/ROP-teams/src/globals.css.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  RefObject,
} from 'react';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Edit,
  Lock,
  Plus,
  Search,
  Trash2,
} from '@retail-ops/panel-shell/fi';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';

// ===========================================================================
// 1. Stylesheet injection (Wave 6b)
// ===========================================================================

/**
 * Idempotent stylesheet injector. The first render of any CrudTable primitive
 * mounts a single `<style id="pcb-crud-table-styles">` block carrying the
 * hover/zebra/shimmer/empty-state rules. We keep this in JS (not globals.css)
 * so the editor module is self-contained — drop CrudTable.tsx onto any new
 * surface and the visuals come along.
 */
let _crudStylesInjected = false;
function ensureCrudTableStyles(): void {
  if (_crudStylesInjected) return;
  if (typeof document === 'undefined') return; // SSR guard
  if (document.getElementById('pcb-crud-table-styles')) {
    _crudStylesInjected = true;
    return;
  }
  const style = document.createElement('style');
  style.id = 'pcb-crud-table-styles';
  style.textContent = `
    /* Wave 6b — modernized CRUD table chrome.
       Scoped strictly by [data-crud-cell] / [data-crud-head] / [data-crud-action]
       so legacy <table> elements elsewhere in the app are untouched. */

    table:has(td[data-crud-cell]) {
      background: var(--surface-card, #ffffff);
      border: 1px solid var(--ink-100, #e5e7eb);
      border-radius: var(--r-md, 8px);
      border-collapse: separate;
      border-spacing: 0;
      overflow: hidden;
      width: 100%;
    }

    /* Sticky header */
    th[data-crud-head] {
      position: sticky;
      top: 0;
      background: var(--ink-040, #f9fafb);
      border-bottom: 1px solid var(--ink-100, #e5e7eb);
      z-index: 1;
    }

    /* Zebra rows */
    tr:has(td[data-crud-cell]):nth-child(even) td[data-crud-cell] {
      background: var(--ink-030, #fafbfc);
    }

    /* Hover state — accent rail + opacity reveal on action cells */
    tr:has(td[data-crud-cell]):hover td[data-crud-cell] {
      background: var(--ink-050, #f3f4f6);
    }
    tr:has(td[data-crud-cell]):hover td[data-crud-cell]:first-child {
      box-shadow: inset 3px 0 0 var(--pcb-blue, #2968ad);
    }

    /* Action cells fade up on hover so the row reads cleanly at rest */
    [data-crud-action] {
      opacity: 0.55;
      transition: opacity 120ms ease-out, background 120ms ease-out;
    }
    tr:hover [data-crud-action],
    [data-crud-action]:focus-visible {
      opacity: 1;
    }
    [data-crud-action]:hover {
      background: var(--ink-100, #e5e7eb);
    }
    [data-crud-action]:focus-visible {
      outline: 2px solid var(--pcb-blue, #2968ad);
      outline-offset: 1px;
    }

    /* Sortable header chevron alignment */
    th[data-crud-head] button.crud-sort {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      background: transparent;
      border: none;
      color: inherit;
      font: inherit;
      cursor: pointer;
      padding: 0;
    }

    /* Skeleton shimmer */
    @keyframes pcb-crud-shimmer {
      0%   { background-position: -200px 0; }
      100% { background-position: calc(200px + 100%) 0; }
    }
    .pcb-crud-skeleton {
      display: inline-block;
      height: 12px;
      width: 80%;
      border-radius: 4px;
      background: linear-gradient(
        90deg,
        var(--ink-100, #e5e7eb) 0px,
        var(--ink-050, #f3f4f6) 60px,
        var(--ink-100, #e5e7eb) 120px
      );
      background-size: 200px 100%;
      background-repeat: no-repeat;
      animation: pcb-crud-shimmer 1.4s ease-in-out infinite;
    }

    /* Toolbar shadow on scroll */
    .pcb-crud-toolbar {
      position: sticky;
      top: 0;
      z-index: 2;
      background: var(--surface-card, #ffffff);
      border-bottom: 1px solid var(--ink-100, #e5e7eb);
    }
  `;
  document.head.appendChild(style);
  _crudStylesInjected = true;
}

// Hook variant — wires the injection into the React lifecycle so unit tests
// running in jsdom pick it up too.
function useEnsureCrudStyles(): void {
  useEffect(() => {
    ensureCrudTableStyles();
  }, []);
}

// ===========================================================================
// 2. Toast
// ===========================================================================

export interface CrudToast {
  tone: 'ok' | 'err' | 'info' | 'warn';
  text: string;
}

export function CrudToastBanner({
  toast,
  onDismiss,
}: {
  toast: CrudToast | null;
  onDismiss: () => void;
}): JSX.Element | null {
  if (!toast) return null;
  return (
    <Banner tone={toast.tone}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <span>{toast.text}</span>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: 12,
            textDecoration: 'underline',
          }}
        >
          dismiss
        </button>
      </div>
    </Banner>
  );
}

// ===========================================================================
// 3. Page header — title + plain-language sentence + optional pill + actions
// ===========================================================================

export interface CrudHeaderProps {
  title: string;
  /** 1–2 sentence plain-language explanation. Always shown. */
  intro: string;
  /** Optional badge to the right of the title (e.g. "Live", "Beta"). */
  badge?: { tone: 'ok' | 'info' | 'warn' | 'muted'; label: string };
  actions?: ReactNode;
}

export function CrudHeader({
  title,
  intro,
  badge,
  actions,
}: CrudHeaderProps): JSX.Element {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 'var(--sp-6, 20px)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 800 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
              color: 'var(--ink-90, #1e2530)',
            }}
          >
            {title}
          </h1>
          {badge && <Pill variant={badge.tone}>{badge.label}</Pill>}
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: 'var(--ink-70, #6b7280)',
            lineHeight: 1.55,
          }}
        >
          {intro}
        </p>
      </div>
      {actions && (
        <div style={{ display: 'flex', gap: 'var(--sp-3, 8px)', alignItems: 'center' }}>
          {actions}
        </div>
      )}
    </header>
  );
}

// ===========================================================================
// 4. Empty state — gentle, plain-language guidance with SVG illustration
// ===========================================================================

/**
 * Modernized empty state. Wave 6b dropped the unicode-emoji placeholder and
 * replaced it with a soft inline SVG that ages well across themes. The
 * "+ Add {singularNoun}" CTA respects the catalog mutability — provider must
 * mount above (default 'full' lets it through).
 */
export function CrudEmptyState({
  pluralNoun,
  singularNoun,
  onAdd,
  /** Custom guidance line beneath the heading. Defaults to "Click ... to create your first one." */
  helpText,
}: {
  pluralNoun: string;
  singularNoun: string;
  onAdd: () => void;
  helpText?: ReactNode;
}): JSX.Element {
  useEnsureCrudStyles();
  return (
    <div
      role="status"
      style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px dashed var(--ink-200, var(--surface-border))',
        borderRadius: 'var(--r-md, 8px)',
        padding: '48px 24px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <svg
        width="80"
        height="80"
        viewBox="0 0 80 80"
        aria-hidden
        focusable="false"
        style={{ opacity: 0.7 }}
      >
        <rect
          x="14"
          y="10"
          width="52"
          height="60"
          rx="6"
          fill="var(--ink-050, #f3f4f6)"
          stroke="var(--ink-200, #d1d5db)"
          strokeWidth="1.5"
        />
        <rect x="22" y="22" width="36" height="3" rx="1.5" fill="var(--ink-200, #d1d5db)" />
        <rect x="22" y="32" width="28" height="3" rx="1.5" fill="var(--ink-200, #d1d5db)" />
        <rect x="22" y="42" width="32" height="3" rx="1.5" fill="var(--ink-200, #d1d5db)" />
        <circle
          cx="58"
          cy="58"
          r="10"
          fill="var(--pcb-blue, #2968ad)"
          opacity="0.12"
        />
        <path
          d="M58 53v10M53 58h10"
          stroke="var(--pcb-blue, #2968ad)"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      <div>
        <div
          style={{
            fontSize: 15,
            color: 'var(--ink-90, #1e2530)',
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          No {pluralNoun} yet.
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-70, #6b7280)' }}>
          {helpText ?? (
            <>
              Click <strong>+ Add {singularNoun}</strong> to create your first one.
            </>
          )}
        </div>
      </div>
      <CrudAddButton onClick={onAdd} singularNoun={singularNoun} />
    </div>
  );
}

// ===========================================================================
// 5. Inline help (subtle muted hint)
// ===========================================================================

export function FieldHelp({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span
      style={{
        fontSize: 11,
        color: 'var(--ink-60, #9ca3af)',
        lineHeight: 1.45,
      }}
    >
      {children}
    </span>
  );
}

// ===========================================================================
// 6. Field — vertical label + child input + optional FieldHelp underneath
// ===========================================================================

export interface FieldProps {
  label: string;
  /** Plain-language hint shown beneath the input. */
  help?: ReactNode;
  /** When `true`, suffix the label with the required asterisk. */
  required?: boolean;
  /** Inline error message — when present, renders red beneath the input. */
  error?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}

export function Field({
  label,
  help,
  required,
  error,
  children,
  style,
}: FieldProps): JSX.Element {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 12,
        color: 'var(--ink-70, #6b7280)',
        ...style,
      }}
    >
      <span>
        {label}
        {required && (
          <span
            style={{ color: 'var(--err-600, #b10505)', marginLeft: 4 }}
            aria-label="required"
          >
            *
          </span>
        )}
      </span>
      {children}
      {error ? (
        <span style={{ fontSize: 11, color: 'var(--err-text, #b10505)', lineHeight: 1.45 }}>
          {error}
        </span>
      ) : (
        help && <FieldHelp>{help}</FieldHelp>
      )}
    </label>
  );
}

// ===========================================================================
// 7. Standardized input/select/textarea styles
// ===========================================================================

export const inputStyle: CSSProperties = {
  padding: '6px 10px',
  fontSize: 13,
  border: '1px solid var(--ink-200, #d1d5db)',
  borderRadius: 4,
  background: 'var(--surface-card, #ffffff)',
  color: 'var(--ink-90, #1e2530)',
  width: '100%',
  fontFamily: 'inherit',
};

export const readOnlyInputStyle: CSSProperties = {
  ...inputStyle,
  background: 'var(--ink-050, #f3f4f6)',
  color: 'var(--ink-70, #6b7280)',
  cursor: 'not-allowed',
};

export const monoInputStyle: CSSProperties = {
  ...inputStyle,
  fontFamily: 'Consolas, Menlo, monospace',
};

// ===========================================================================
// 8. Table primitives — Th / Td (Wave 6b modernized)
// ===========================================================================

export function Th({
  children,
  style,
  sortable,
  sortDir,
  onSort,
  scope = 'col',
}: {
  children?: ReactNode;
  style?: CSSProperties;
  /** When true, renders the column header as a button + chevron. */
  sortable?: boolean;
  /** Current sort direction; `null` means "unsorted". */
  sortDir?: 'asc' | 'desc' | null;
  onSort?: () => void;
  scope?: 'col' | 'row';
}): JSX.Element {
  useEnsureCrudStyles();
  return (
    <th
      data-crud-head
      scope={scope}
      style={{
        padding: '10px 12px',
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--ink-70, #6b7280)',
        textAlign: 'left',
        whiteSpace: 'nowrap',
        ...(style ?? {}),
      }}
    >
      {sortable && onSort ? (
        <button type="button" className="crud-sort" onClick={onSort}>
          <span>{children}</span>
          {sortDir === 'asc' ? (
            <ChevronUp size={12} aria-hidden />
          ) : sortDir === 'desc' ? (
            <ChevronDown size={12} aria-hidden />
          ) : (
            <ChevronsUpDown size={12} aria-hidden style={{ opacity: 0.6 }} />
          )}
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export function Td({
  children,
  style,
  title,
  colSpan,
  onClick,
}: {
  children?: ReactNode;
  style?: CSSProperties;
  title?: string;
  colSpan?: number;
  onClick?: (event: ReactMouseEvent<HTMLTableCellElement>) => void;
}): JSX.Element {
  useEnsureCrudStyles();
  return (
    <td
      data-crud-cell
      title={title}
      colSpan={colSpan}
      onClick={onClick}
      style={{
        padding: '10px 12px',
        verticalAlign: 'middle',
        color: 'var(--ink-90, #1e2530)',
        fontSize: 13,
        borderTop: '1px solid var(--ink-100, #e5e7eb)',
        ...(style ?? {}),
      }}
    >
      {children}
    </td>
  );
}

// ===========================================================================
// 9. CrudGrid — opinionated <table> + sticky-header wrapper (Wave 6b)
// ===========================================================================

export function CrudGrid({
  children,
  style,
  ariaLabel,
}: {
  children: ReactNode;
  style?: CSSProperties;
  ariaLabel?: string;
}): JSX.Element {
  useEnsureCrudStyles();
  return (
    <div
      style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--ink-100, var(--surface-border))',
        borderRadius: 'var(--r-md, 8px)',
        overflow: 'auto',
        ...style,
      }}
    >
      <table aria-label={ariaLabel} style={{ width: '100%', borderCollapse: 'collapse' }}>
        {children}
      </table>
    </div>
  );
}

/** Right-aligned column header dedicated to the row-actions cell. */
export function CrudGridActionsCell({
  children,
  style,
}: {
  children?: ReactNode;
  style?: CSSProperties;
}): JSX.Element {
  return (
    <Th style={{ textAlign: 'right', width: '1%', whiteSpace: 'nowrap', ...style }}>
      {children ?? <span style={{ visibility: 'hidden' }}>Actions</span>}
    </Th>
  );
}

// ===========================================================================
// 10. CrudPage — page wrapper component, semantic <main>
// ===========================================================================

export function CrudPage({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}): JSX.Element {
  useEnsureCrudStyles();
  return (
    <main
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-6, 20px)',
        padding: 'var(--sp-8, 32px)',
        maxWidth: 1280,
        margin: '0 auto',
        width: '100%',
        ...style,
      }}
    >
      {children}
    </main>
  );
}

// ===========================================================================
// 11. Toolbar primitives (Wave 6b) — search input + result count + sticky bar
// ===========================================================================

export function CrudSearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  autoFocus,
  width = 320,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  width?: number | string;
}): JSX.Element {
  useEnsureCrudStyles();
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        width,
      }}
    >
      <Search
        size={14}
        aria-hidden
        style={{
          position: 'absolute',
          left: 10,
          color: 'var(--ink-60, #9ca3af)',
          pointerEvents: 'none',
        }}
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? 'Search…'}
        aria-label={ariaLabel ?? 'Search'}
        autoFocus={autoFocus}
        style={{
          ...inputStyle,
          paddingLeft: 30,
        }}
      />
    </div>
  );
}

export function CrudResultCount({
  visible,
  total,
  noun = 'rows',
}: {
  visible: number;
  total: number;
  noun?: string;
}): JSX.Element {
  return (
    <span
      style={{
        fontSize: 12,
        color: 'var(--ink-70, #6b7280)',
        whiteSpace: 'nowrap',
      }}
    >
      Showing <strong style={{ color: 'var(--ink-90)' }}>{visible}</strong> of {total} {noun}
    </span>
  );
}

export function CrudToolbar({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}): JSX.Element {
  useEnsureCrudStyles();
  return (
    <div
      className="pcb-crud-toolbar"
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        padding: '10px 4px',
        flexWrap: 'wrap',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ===========================================================================
// 12. Skeleton rows (Wave 6b)
// ===========================================================================

export function CrudSkeletonRows({
  rows = 5,
  cols,
}: {
  rows?: number;
  cols: number;
}): JSX.Element {
  useEnsureCrudStyles();
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }).map((__, c) => (
            <Td key={c}>
              <span className="pcb-crud-skeleton" aria-hidden />
            </Td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ===========================================================================
// 13. Row action primitives (Wave 6b)
// ===========================================================================

/**
 * Flex container that right-aligns row-action icon buttons. The action buttons
 * inside fade in on row hover via the [data-crud-action] CSS rule.
 */
export function CrudRowActions({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'inline-flex',
        gap: 4,
        justifyContent: 'flex-end',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

interface CrudIconButtonProps {
  onClick: () => void;
  ariaLabel: string;
  title?: string;
  disabled?: boolean;
}

export function CrudEditButton({
  onClick,
  ariaLabel,
  title,
  disabled,
}: CrudIconButtonProps): JSX.Element {
  useEnsureCrudStyles();
  return (
    <button
      type="button"
      data-crud-action
      onClick={onClick}
      aria-label={ariaLabel}
      title={title ?? ariaLabel}
      disabled={disabled}
      style={{
        width: 32,
        height: 32,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        borderRadius: 4,
        color: 'var(--ink-80, #4b5563)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <Edit size={16} aria-hidden />
    </button>
  );
}

/**
 * Delete row-action button. Auto-hides when the surrounding
 * <CatalogMutabilityProvider> reports anything other than 'full' (e.g.
 * 'append-only' or 'system'). Editors don't need to thread a `disabled` prop.
 */
export function CrudDeleteButton({
  onClick,
  ariaLabel,
  title,
  disabled,
}: CrudIconButtonProps): JSX.Element | null {
  useEnsureCrudStyles();
  const mutability = useCatalogMutability();
  if (mutability !== 'full') return null;
  return (
    <button
      type="button"
      data-crud-action
      onClick={onClick}
      aria-label={ariaLabel}
      title={title ?? ariaLabel}
      disabled={disabled}
      style={{
        width: 32,
        height: 32,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        borderRadius: 4,
        color: 'var(--err-text, #b10505)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <Trash2 size={16} aria-hidden />
    </button>
  );
}

/**
 * Primary "+ Add {singular}" CTA. Auto-hides when catalog mutability is
 * 'system' (a fully read-only / system-managed catalog). On 'append-only'
 * catalogs the button stays visible (you can still add rows, just not delete).
 */
export function CrudAddButton({
  onClick,
  singularNoun,
  disabled,
  label,
}: {
  onClick: () => void;
  singularNoun: string;
  disabled?: boolean;
  /** Override the default "+ Add {singularNoun}" label. */
  label?: string;
}): JSX.Element | null {
  const mutability = useCatalogMutability();
  if (mutability === 'system') return null;
  return (
    <Button
      variant="primary"
      onClick={onClick}
      disabled={disabled}
      startIcon={<Plus size={14} aria-hidden />}
    >
      {label ?? `Add ${singularNoun}`}
    </Button>
  );
}

/** Small variant of Pill — 11px text + tighter padding for in-table tags. */
export function CrudCompactPill({
  variant,
  children,
  icon,
}: {
  variant: 'ok' | 'warn' | 'err' | 'info' | 'muted';
  children: ReactNode;
  icon?: ReactNode;
}): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 999,
        background:
          variant === 'ok'
            ? 'var(--ok-bg, #d1fae5)'
            : variant === 'warn'
              ? 'var(--warn-bg, #fef3c7)'
              : variant === 'err'
                ? 'var(--err-bg, #fee2e2)'
                : variant === 'info'
                  ? 'var(--info-bg, #dbeafe)'
                  : 'var(--ink-100, #e5e7eb)',
        color:
          variant === 'ok'
            ? 'var(--ok-text, #065f46)'
            : variant === 'warn'
              ? 'var(--warn-text, #92400e)'
              : variant === 'err'
                ? 'var(--err-text, #991b1b)'
                : variant === 'info'
                  ? 'var(--info-text, #1e40af)'
                  : 'var(--ink-80, #4b5563)',
      }}
    >
      {icon}
      {children}
    </span>
  );
}

// ===========================================================================
// 14. Catalog mutability (Wave 6)
// ===========================================================================

/**
 * Mutability tier governs which CRUD affordances the editor exposes.
 *   - 'system'      : read-only. Add + Delete are hidden. Edit may still be
 *                     allowed for owner-blessed metadata fields (the editor
 *                     decides — this primitive just hides the destructive
 *                     affordances).
 *   - 'append-only' : Add is shown, Delete is hidden. Edits to existing rows
 *                     are allowed. Used for audit-bearing catalogs where rows
 *                     accrue but never truly disappear (deactivation only).
 *   - 'full'        : default. All three affordances visible.
 */
export type CatalogMutability = 'system' | 'append-only' | 'full';

const CatalogMutabilityContext = createContext<CatalogMutability>('full');

export function CatalogMutabilityProvider({
  value,
  children,
}: {
  value: CatalogMutability;
  children: ReactNode;
}): JSX.Element {
  return (
    <CatalogMutabilityContext.Provider value={value}>
      {children}
    </CatalogMutabilityContext.Provider>
  );
}

export function useCatalogMutability(): CatalogMutability {
  return useContext(CatalogMutabilityContext);
}

/**
 * Renders a plain-language Banner above editors when the catalog is
 * system-managed or append-only, explaining why the destructive affordances
 * are missing. Renders nothing on 'full' catalogs (the default).
 */
export function CatalogMutabilityBanner({
  catalogName,
}: {
  catalogName?: string;
}): JSX.Element | null {
  const mutability = useCatalogMutability();
  if (mutability === 'full') return null;
  const name = catalogName ?? 'this catalog';
  if (mutability === 'system') {
    return (
      <Banner tone="info" icon={<Lock size={14} aria-hidden />} title="System-managed catalog">
        <span>
          {name} is owned by the system and cannot be added to or deleted here. Edits to
          existing rows may still be allowed for descriptive fields. Reach out to the system
          owner for structural changes.
        </span>
      </Banner>
    );
  }
  // append-only
  return (
    <Banner tone="info" icon={<Lock size={14} aria-hidden />} title="Append-only catalog">
      <span>
        {name} keeps a permanent audit trail, so rows can be added or deactivated but never
        deleted outright. Use the "Deactivate" or status toggle on each row.
      </span>
    </Banner>
  );
}

// ===========================================================================
// 15. Dirty tracking (Wave 4) — useDirtyTracker<T>
// ===========================================================================

/**
 * Tracks a draft object against its initial snapshot. Returns
 * `{ state, setState, isDirty, reset }`. Used by editors that want to disable
 * the Save button until the user actually changes something, or to gate
 * close-with-unsaved-changes warnings.
 */
export function useDirtyTracker<T>(initial: T): {
  state: T;
  setState: (next: T) => void;
  isDirty: boolean;
  reset: (newInitial?: T) => void;
} {
  const [snapshot, setSnapshot] = useState<T>(initial);
  const [state, setStateRaw] = useState<T>(initial);

  const isDirty = useMemo(() => {
    try {
      return JSON.stringify(snapshot) !== JSON.stringify(state);
    } catch {
      return snapshot !== state;
    }
  }, [snapshot, state]);

  const setState = useCallback((next: T) => setStateRaw(next), []);
  const reset = useCallback((newInitial?: T) => {
    const v = newInitial ?? snapshot;
    setSnapshot(v);
    setStateRaw(v);
  }, [snapshot]);

  return { state, setState, isDirty, reset };
}

// ===========================================================================
// 16. Concurrency primitives (Wave 4)
// ===========================================================================

/**
 * Marker interface — any DTO carrying optimistic-concurrency metadata is
 * compatible with `useConcurrencyDraft` / `buildConcurrencyEnvelope`. The two
 * common shapes are SQL `rowVersion` (rowversion timestamp) and ISO
 * `updatedAt` strings.
 */
export interface ConcurrencyAware {
  updatedAt?: string | null;
  rowVersion?: string | null;
  /** Legacy alias used by some editors. */
  __updatedAt?: string | null;
}

/**
 * Outcome of `detectConcurrencyConflict()`. `isConflict` is the field editor
 * code typically branches on; the other fields surface the diff for the
 * Banner the user sees.
 */
export interface ConflictDetectionResult {
  isConflict: boolean;
  /** Server-reported updatedAt the row has on disk. */
  serverUpdatedAt?: string | null;
  /** Server-reported rowVersion the row has on disk. */
  serverRowVersion?: string | null;
  /** Human-readable explanation for the Banner. */
  message?: string;
  /** Optional raw server payload — null on transport errors. */
  server?: unknown;
  /** Optional client snapshot at conflict time. */
  client?: unknown;
}

/**
 * Wraps a draft DTO with concurrency-aware state. `commit(next)` snapshots the
 * server response (so subsequent saves pass the new etag), `discard()` rolls
 * back to the last known good. Editors that only need dirty-tracking can use
 * `useDirtyTracker` directly.
 */
export function useConcurrencyDraft<T extends ConcurrencyAware>(initial: T): {
  draft: T;
  setDraft: (next: T) => void;
  commit: (serverResponse: T) => void;
  discard: () => void;
  isDirty: boolean;
} {
  const initialRef = useRef<T>(initial);
  const [draft, setDraft] = useState<T>(initial);

  const isDirty = useMemo(() => {
    try {
      return JSON.stringify(initialRef.current) !== JSON.stringify(draft);
    } catch {
      return initialRef.current !== draft;
    }
  }, [draft]);

  const commit = useCallback((serverResponse: T) => {
    initialRef.current = serverResponse;
    setDraft(serverResponse);
  }, []);
  const discard = useCallback(() => {
    setDraft(initialRef.current);
  }, []);

  return { draft, setDraft, commit, discard, isDirty };
}

/**
 * Builds the request body + headers carrying optimistic-concurrency tokens.
 * `expectedUpdatedAt` / `expectedRowVersion` are mirrored into both the body
 * (for servers that read them from JSON) and `If-Match` (for servers that
 * read them from HTTP headers).
 *
 * Degrades to last-writer-wins when the original row carries no token — the
 * server will accept the write and the editor reloads to pick up the new
 * etag on the next render.
 */
export function buildConcurrencyEnvelope<TBody extends object>(
  original: ConcurrencyAware | null | undefined,
  body: TBody,
): { body: TBody & { expectedUpdatedAt?: string; expectedRowVersion?: string }; headers: Record<string, string> } {
  const headers: Record<string, string> = {};
  const augmentedBody: TBody & { expectedUpdatedAt?: string; expectedRowVersion?: string } = {
    ...body,
  };
  if (!original) return { body: augmentedBody, headers };

  const upd = original.updatedAt ?? original.__updatedAt ?? null;
  const rv = original.rowVersion ?? null;
  if (upd) {
    augmentedBody.expectedUpdatedAt = upd;
    headers['If-Match'] = upd;
  }
  if (rv) {
    augmentedBody.expectedRowVersion = rv;
    if (!headers['If-Match']) headers['If-Match'] = rv;
  }
  return { body: augmentedBody, headers };
}

/**
 * Parses a thrown error from a save call into a structured
 * ConflictDetectionResult. Recognizes:
 *   - HTTP 409 (Conflict) responses with JSON bodies carrying serverUpdatedAt
 *   - Server-side guard strings like "concurrency conflict" / "expectedUpdatedAt mismatch"
 * For any other error returns `{ isConflict: false }` and the caller surfaces
 * the message via the usual toast.
 */
export function detectConcurrencyConflict(error: unknown): ConflictDetectionResult {
  if (!error || typeof error !== 'object') return { isConflict: false };
  const e = error as Record<string, unknown>;

  // Common ApiError shapes
  const status = typeof e.status === 'number' ? (e.status as number) : undefined;
  const code = typeof e.code === 'string' ? (e.code as string) : undefined;
  const message = typeof e.message === 'string' ? (e.message as string) : undefined;
  const body =
    typeof e.body === 'object' && e.body !== null
      ? (e.body as Record<string, unknown>)
      : undefined;

  const looksLikeConflict =
    status === 409 ||
    code === 'concurrency-conflict' ||
    code === 'CONFLICT' ||
    (typeof message === 'string' &&
      /concurrency|conflict|expectedUpdatedAt|expectedRowVersion|If-Match/i.test(message));

  if (!looksLikeConflict) return { isConflict: false };

  const serverUpdatedAt =
    (body && typeof body['serverUpdatedAt'] === 'string'
      ? (body['serverUpdatedAt'] as string)
      : undefined) ?? null;
  const serverRowVersion =
    (body && typeof body['serverRowVersion'] === 'string'
      ? (body['serverRowVersion'] as string)
      : undefined) ?? null;

  return {
    isConflict: true,
    serverUpdatedAt,
    serverRowVersion,
    message:
      message ??
      'Another admin saved a newer version of this row while you were editing it.',
    server: body ?? null,
  };
}

/**
 * Minimal client shape that satisfies emitAdminConflictEvent's needs. The
 * editor's `useApiClient()` instance is structurally compatible — we cast at
 * the call site rather than importing the full ApiClient type to avoid a
 * circular dep.
 */
interface AdminConflictApiClient {
  http: {
    post: <T>(path: string, init: { body: unknown }) => Promise<T>;
  };
}

/**
 * Fire-and-forget POST to the admin session-events sink. Failures are
 * swallowed (best-effort telemetry). The server-side handler dedupes by
 * (catalog, rowKey, actor) over a short window so retries are safe.
 */
export function emitAdminConflictEvent(
  client: AdminConflictApiClient,
  payload: {
    catalog: string;
    rowKey: string;
    actor: string;
    serverUpdatedAt: string | null;
    message?: string;
  },
): void {
  try {
    void client.http
      .post('/v3/admin/session-events', {
        body: {
          eventType: 'admin.concurrency.conflict',
          ...payload,
          at: new Date().toISOString(),
        },
      })
      .catch(() => undefined);
  } catch {
    /* fire-and-forget */
  }
}

/**
 * Renders a "this row was changed by another admin — reload?" banner. The
 * Banner is suppressed when `conflict` is null, so editors can mount this
 * unconditionally above their table.
 */
export function CrudConcurrencyBanner({
  conflict,
  onReload,
  onDismiss,
}: {
  conflict: ConflictDetectionResult | null;
  onReload: () => void;
  onDismiss: () => void;
}): JSX.Element | null {
  if (!conflict || !conflict.isConflict) return null;
  return (
    <Banner tone="warn" title="Another admin changed this row">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <span>
          {conflict.message ??
            'Your changes were not saved because the row on the server is newer than the one you opened.'}
          {conflict.serverUpdatedAt && (
            <>
              {' '}Server version: <code>{conflict.serverUpdatedAt}</code>.
            </>
          )}
        </span>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          <Button size="sm" variant="primary" onClick={onReload}>
            Reload from server
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            Keep my edits
          </Button>
        </div>
      </div>
    </Banner>
  );
}

// ===========================================================================
// 17. Audit footer (Wave 4)
// ===========================================================================

function formatAuditTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso ?? '—';
  }
}

/**
 * Small row at the bottom of edit forms summarizing the row's audit chain.
 * Renders both createdBy/createdAt and updatedBy/updatedAt when available.
 */
export function AuditRowFooter({
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
  style,
}: {
  createdAt?: string | null;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  style?: CSSProperties;
}): JSX.Element | null {
  if (!createdAt && !updatedAt) return null;
  return (
    <div
      style={{
        fontSize: 11,
        color: 'var(--ink-60, #9ca3af)',
        borderTop: '1px solid var(--ink-100, var(--surface-border))',
        paddingTop: 8,
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        ...style,
      }}
    >
      {createdAt && (
        <span>
          Created {createdBy ? <>by <strong>{createdBy}</strong> </> : ''}on {formatAuditTime(createdAt)}
        </span>
      )}
      {updatedAt && (
        <span>
          Updated {updatedBy ? <>by <strong>{updatedBy}</strong> </> : ''}on {formatAuditTime(updatedAt)}
        </span>
      )}
    </div>
  );
}

// ===========================================================================
// 18. Validated text field (Wave 4)
// ===========================================================================

export function ValidatedField({
  label,
  value,
  onChange,
  required,
  help,
  pattern,
  patternMessage,
  minLength,
  maxLength,
  placeholder,
  inputType = 'text',
  disabled,
  monospace,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  help?: ReactNode;
  /** Regex source — leave undefined to skip pattern validation. */
  pattern?: RegExp;
  patternMessage?: string;
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
  inputType?: 'text' | 'email' | 'tel' | 'url' | 'number';
  disabled?: boolean;
  monospace?: boolean;
}): JSX.Element {
  const error = useMemo<string | null>(() => {
    if (required && value.trim().length === 0) return 'Required.';
    if (typeof minLength === 'number' && value.length < minLength)
      return `Must be at least ${minLength} characters.`;
    if (typeof maxLength === 'number' && value.length > maxLength)
      return `Must be at most ${maxLength} characters.`;
    if (pattern && value.length > 0 && !pattern.test(value))
      return patternMessage ?? 'Invalid format.';
    return null;
  }, [value, required, minLength, maxLength, pattern, patternMessage]);

  return (
    <Field label={label} required={required} help={help} error={error}>
      <input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        style={monospace ? monoInputStyle : inputStyle}
      />
    </Field>
  );
}

// ===========================================================================
// 19. JSON textarea (Wave 4)
// ===========================================================================

export function JsonValidatedField({
  label,
  value,
  onChange,
  required,
  help,
  helpText,
  rows = 6,
  disabled,
  submitAttempts,
  style,
  /** When provided, runs after JSON.parse to validate the shape. Returns null
      for "valid" or a message string for the error banner. */
  validateShape,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  help?: ReactNode;
  /** Alias for `help` — caller-friendly name. */
  helpText?: ReactNode;
  rows?: number;
  disabled?: boolean;
  /** Save-attempt counter that gates the required-but-empty error. */
  submitAttempts?: number;
  style?: CSSProperties;
  validateShape?: (parsed: unknown) => string | null;
}): JSX.Element {
  const attemptsCount = submitAttempts ?? 1;
  const resolvedHelp: ReactNode = help ?? helpText;
  const error = useMemo<string | null>(() => {
    if (attemptsCount > 0 && required && value.trim().length === 0) return 'Required.';
    if (value.trim().length === 0) return null;
    try {
      const parsed = JSON.parse(value);
      if (validateShape) return validateShape(parsed);
      return null;
    } catch (e) {
      return e instanceof Error ? `Invalid JSON: ${e.message}` : 'Invalid JSON.';
    }
  }, [value, required, validateShape, attemptsCount]);

  return (
    <Field label={label} required={required} help={resolvedHelp} error={error} style={style}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        disabled={disabled}
        spellCheck={false}
        style={{ ...monoInputStyle, resize: 'vertical', minHeight: rows * 18 }}
      />
    </Field>
  );
}

// ===========================================================================
// 20. Numeric range field (Wave 4)
// ===========================================================================

export function NumericRangeField({
  label,
  value,
  onChange,
  required,
  help,
  helpText,
  min,
  max,
  step = 1,
  placeholder,
  disabled,
  units,
  unit,
  integerOnly,
  submitAttempts,
  style,
}: {
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
  required?: boolean;
  help?: ReactNode;
  /** Alias for `help` — accepted so caller code that uses helpText compiles. */
  helpText?: ReactNode;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  /** Right-side adornment, e.g. "USD", "%", "minutes". */
  units?: string;
  /** Alias for `units` — accepted so caller code that uses unit compiles. */
  unit?: string;
  /**
   * When true, the parsed value is clamped to an integer (no decimals). The
   * `step` defaults to 1 already; this also rejects non-integer typed input.
   */
  integerOnly?: boolean;
  /** Number of save attempts — gates the required-but-empty error. */
  submitAttempts?: number;
  style?: CSSProperties;
}): JSX.Element {
  const attemptsCount = submitAttempts ?? 1;
  const resolvedHelp: ReactNode = help ?? helpText;
  const resolvedUnits: string | undefined = units ?? unit;
  const raw = value === null || value === undefined ? '' : String(value);
  const error = useMemo<string | null>(() => {
    if (attemptsCount > 0 && required && (value === null || value === undefined)) return 'Required.';
    if (value === null || value === undefined) return null;
    if (Number.isNaN(value)) return 'Must be a number.';
    if (integerOnly && !Number.isInteger(value)) return 'Must be a whole number.';
    if (typeof min === 'number' && value < min) return `Must be at least ${min}.`;
    if (typeof max === 'number' && value > max) return `Must be at most ${max}.`;
    return null;
  }, [value, required, min, max, integerOnly, attemptsCount]);

  return (
    <Field label={label} required={required} help={resolvedHelp} error={error} style={style}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <input
          type="number"
          value={raw}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') {
              onChange(null);
              return;
            }
            const n = Number(v);
            if (Number.isNaN(n)) {
              onChange(null);
              return;
            }
            onChange(integerOnly ? Math.trunc(n) : n);
          }}
          min={min}
          max={max}
          step={integerOnly ? 1 : step}
          placeholder={placeholder}
          disabled={disabled}
          style={inputStyle}
        />
        {resolvedUnits && (
          <span style={{ fontSize: 12, color: 'var(--ink-70)', whiteSpace: 'nowrap' }}>
            {resolvedUnits}
          </span>
        )}
      </div>
    </Field>
  );
}

// ===========================================================================
// 21. FK select field (Wave 4)
// ===========================================================================

export interface FkSelectOption {
  code: string;
  label: string;
  disabled?: boolean;
}

export function FkSelectField({
  label,
  value,
  onChange,
  options,
  required,
  helpText,
  placeholder,
  disabled,
  loading,
  submitAttempts,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: readonly FkSelectOption[];
  required?: boolean;
  /** Plain-language hint shown beneath the select. */
  helpText?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /**
   * Number of save attempts the parent form has made. When 0, the
   * required-but-empty error stays hidden so first-time-open forms don't
   * shout at the user before they've tried to save. Increment after each
   * Save click. Defaults to 1 so existing callers (no submitAttempts
   * threading) keep their pre-existing eager-validation behavior.
   */
  submitAttempts?: number;
}): JSX.Element {
  const attemptsCount = submitAttempts ?? 1;
  const error = useMemo<string | null>(() => {
    if (attemptsCount <= 0) return null;
    if (required && (!value || value.trim().length === 0)) return 'Required.';
    return null;
  }, [value, required, attemptsCount]);

  return (
    <Field label={label} required={required} help={helpText} error={error}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading}
        style={inputStyle}
      >
        <option value="" disabled={required}>
          {loading ? 'Loading…' : (placeholder ?? '— pick one —')}
        </option>
        {options.map((opt) => (
          <option key={opt.code} value={opt.code} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

// ===========================================================================
// 22. Type-name-to-confirm-delete modal (Wave 4)
// ===========================================================================

/**
 * Modal that requires the user to type the row's name verbatim before the
 * Delete button enables. Used for catalogs where an accidental delete would
 * be expensive to recover from.
 *
 * Pair with `confirmDeleteByName()` for a Promise-style API the editor can
 * `await` inside a handler.
 */
export function TypeNameToConfirmDelete({
  open,
  name,
  rowIdentifier,
  friendlyType,
  title,
  onConfirm,
  onCancel,
  busy,
  consequenceNote,
  warningCopy,
  preview,
  confirmLabel,
}: {
  open: boolean;
  /** Verbatim row name — the user must type this. */
  name?: string;
  /**
   * Alias for `name` — accepted so callers that thread the row id (which may
   * not be the display name) compile without rename. When both are
   * supplied, `rowIdentifier` wins.
   */
  rowIdentifier?: string;
  /** Plain-language category, e.g. "Branch", "Card Type". */
  friendlyType?: string;
  /** Optional title override; default is `Delete {friendlyType}?`. */
  title?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  /** Optional extra sentence under the headline explaining downstream impact. */
  consequenceNote?: ReactNode;
  /**
   * Optional warning copy that REPLACES the default
   * "You're about to delete {name}…" paragraph. Use this when the row
   * impact requires more than a generic line.
   */
  warningCopy?: ReactNode;
  /**
   * Optional preview block rendered ABOVE the confirm input — useful when
   * the row has dependents (a child-count summary, a sample of affected
   * downstream rows, etc.).
   */
  preview?: ReactNode;
  /** Optional confirm-button label override; default is `Delete {friendlyType}`. */
  confirmLabel?: string;
}): JSX.Element | null {
  const resolvedName = (rowIdentifier ?? name ?? '').toString();
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  if (!open) return null;
  const matches = typed.trim() === resolvedName.trim();
  const resolvedFriendlyType = friendlyType ?? 'row';
  const resolvedTitle = title ?? `Delete ${resolvedFriendlyType}?`;
  const resolvedConfirm = confirmLabel ?? `Delete ${resolvedFriendlyType}`;

  return (
    <ModalShell
      open={open}
      onClose={onCancel}
      title={resolvedTitle}
      size="sm"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={!matches || busy}>
            {busy ? 'Deleting…' : resolvedConfirm}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {warningCopy ? (
          <div style={{ fontSize: 13, color: 'var(--ink-90)' }}>{warningCopy}</div>
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-90)' }}>
            You're about to delete <strong>{resolvedName}</strong>. This can't be undone from the UI.
          </p>
        )}
        {preview}
        {consequenceNote && (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-70)' }}>{consequenceNote}</p>
        )}
        <Field
          label={`Type the ${resolvedFriendlyType.toLowerCase()} name to confirm`}
          help="Capitalization and spacing must match exactly."
        >
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={resolvedName}
            autoFocus
            style={inputStyle}
          />
        </Field>
      </div>
    </ModalShell>
  );
}

/**
 * Promise-style wrapper that pops a TypeNameToConfirmDelete dialog and
 * resolves with `true` on confirm, `false` on cancel. Renders into a
 * dedicated container appended to <body> so the modal escapes any parent
 * overflow:hidden / transform stacking-context issues.
 *
 * NOTE — uses ReactDOM.createRoot under the hood; the caller is expected to
 * `await` the Promise so the modal's lifetime is tied to the call.
 */
export async function confirmDeleteByName(opts: {
  name: string;
  friendlyType: string;
  consequenceNote?: ReactNode;
  onConfirm?: () => void | Promise<void>;
}): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const { createRoot } = await import('react-dom/client');

  return new Promise<boolean>((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);

    const cleanup = (): void => {
      try {
        root.unmount();
      } catch {
        /* noop */
      }
      try {
        host.remove();
      } catch {
        /* noop */
      }
    };

    const handleConfirm = async (): Promise<void> => {
      try {
        await opts.onConfirm?.();
      } finally {
        cleanup();
        resolve(true);
      }
    };
    const handleCancel = (): void => {
      cleanup();
      resolve(false);
    };

    root.render(
      <TypeNameToConfirmDelete
        open
        name={opts.name}
        friendlyType={opts.friendlyType}
        consequenceNote={opts.consequenceNote}
        onConfirm={() => {
          void handleConfirm();
        }}
        onCancel={handleCancel}
      />,
    );
  });
}

// ===========================================================================
// 23. Form-modal wrapper (Wave 4)
// ===========================================================================

/**
 * Wraps ModalShell with a form element so Enter triggers the Save button and
 * Esc cancels. The dirty-state and concurrency-conflict bits stay on the
 * caller — this wrapper is intentionally thin so editors that want full
 * control over their layout can opt out by reverting to ModalShell directly.
 */
export function CrudFormModal({
  open,
  title,
  size = 'md',
  onClose,
  onSubmit,
  saving,
  saveLabel,
  cancelLabel = 'Cancel',
  saveDisabled,
  children,
  footerExtras,
  initialFocusRef,
}: {
  open: boolean;
  title: string;
  size?: 'sm' | 'md' | 'lg';
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  saving?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  saveDisabled?: boolean;
  children: ReactNode;
  /** Extra nodes rendered between Cancel and Save (e.g. a "Delete" link). */
  footerExtras?: ReactNode;
  /** Element to receive focus on open. */
  initialFocusRef?: RefObject<HTMLElement>;
}): JSX.Element | null {
  useEffect(() => {
    if (open && initialFocusRef?.current) {
      const el = initialFocusRef.current;
      // Defer to next tick so ModalShell has finished its own focus dance.
      const id = window.setTimeout(() => el.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open, initialFocusRef]);

  if (!open) return null;
  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      size={size}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ display: 'inline-flex', gap: 8 }}>{footerExtras}</div>
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              {cancelLabel}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                void onSubmit();
              }}
              disabled={saving || saveDisabled}
            >
              {saving ? 'Saving…' : (saveLabel ?? 'Save')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!saving && !saveDisabled) void onSubmit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {children}
        {/* Hidden submit so Enter inside any input triggers onSubmit. */}
        <button type="submit" style={{ display: 'none' }} aria-hidden tabIndex={-1} />
      </form>
    </ModalShell>
  );
}

// ===========================================================================
// 24. SQL helpers — shared between editors that POST raw seed SQL
// ===========================================================================

export function sqlStr(v: string | null | undefined): string {
  if (v === null || v === undefined) return 'NULL';
  return `N'${v.replace(/'/g, "''")}'`;
}

export function sqlInt(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v) || !Number.isFinite(v))
    return 'NULL';
  return String(Math.trunc(v));
}

export function sqlDecimal(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v) || !Number.isFinite(v))
    return 'NULL';
  return String(v);
}

export function sqlBit(v: boolean | undefined | null): string {
  if (v === null || v === undefined) return 'NULL';
  return v ? '1' : '0';
}

export function sqlDate(v: string | null | undefined): string {
  if (!v) return 'NULL';
  return `CAST(${sqlStr(v)} AS DATE)`;
}

export function encodeBase64Utf8(input: string): string {
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    const bytes = new TextEncoder().encode(input);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
    return window.btoa(bin);
  }
  // Node fallback (vitest / SSR) — Buffer is globally available.
  return Buffer.from(input, 'utf8').toString('base64');
}

// ===========================================================================
// 25. Admin auth bypass-key prompt (legacy fallback, Wave 13)
// ===========================================================================

const SESSION_BYPASS_KEY = 'pcb.admin.configSeedBypass';

export function readBypassKey(): string | null {
  try {
    return sessionStorage.getItem(SESSION_BYPASS_KEY);
  } catch {
    return null;
  }
}

export function writeBypassKey(value: string): void {
  try {
    sessionStorage.setItem(SESSION_BYPASS_KEY, value);
  } catch {
    /* sessionStorage unavailable — non-fatal */
  }
}

// ── Wave 13 (admin-auth-hardening) ───────────────────────────────────────
// Module-level flag the AuthContext flips when a Teams SSO bearer token is
// available. When set, `promptBypassKey()` skips the modal prompt entirely
// and returns the TOKEN_SENTINEL, signaling api-client to OMIT the
// `x-config-seed-bypass` header so the bearer-token role-claim path is the
// sole auth carrier (server-side AdminAuthService validates the role).
//
// The bypass-key flow is preserved verbatim for environments where the
// token is unavailable (browser-direct dev, MSAL outage, Entra app not yet
// granted the App ID URI, etc.) so admin work isn't blocked during the
// soak period.
//
// IMPORTANT: the sentinel is a non-empty string ('__token__') so existing
// editor checks like `if (!bypass) return` keep working. The api-client's
// `buildAdminAuthHeaders` recognizes the sentinel and omits the bypass
// header. See `packages/api-client/src/admin-auth.ts`.
export const ADMIN_AUTH_TOKEN_SENTINEL = '__token__';
let _tokenAvailableForAdmin = false;

/**
 * Called by AuthContext whenever the user's bearer token resolves (or
 * becomes unavailable). When `true`, subsequent `promptBypassKey()` calls
 * return the empty-string sentinel without prompting — the api-client's
 * auto-injected `Authorization: Bearer <jwt>` carries auth instead.
 */
export function setAdminAuthTokenAvailable(available: boolean): void {
  _tokenAvailableForAdmin = available;
}

export function isAdminAuthTokenAvailable(): boolean {
  return _tokenAvailableForAdmin;
}

/**
 * Returns the credential to attach to admin write calls. When a bearer
 * token is available (Wave 13 happy path), returns the sentinel — the
 * api-client header helper omits the bypass header entirely. Otherwise
 * falls back to the legacy session-cached prompt flow.
 *
 * Returns `null` only if the user dismisses the prompt and no token is
 * available — caller should abort the write and surface "auth required".
 */
export function promptBypassKey(): string | null {
  // Bill 2026-04-26 — the user should NEVER see a prompt for a "bypass key."
  // It's a deployment-time secret in Key Vault, not something a user knows.
  // Auth flow now:
  //   1. If a stored bypass-key exists (legacy, set by an SRE in earlier
  //      sessions) — use it.
  //   2. Otherwise return the sentinel. The api-client's
  //      `buildAdminAuthHeaders` then omits the bypass-key header. The
  //      server's AdminAuthService accepts the request via Path C
  //      (officer-code header + role lookup against UserRoleAssignments).
  //   3. No browser prompt is ever shown — if the server rejects, the
  //      editor's normal error toast surfaces a real diagnostic instead of
  //      asking the user for a secret they don't have.
  if (typeof window === 'undefined') return ADMIN_AUTH_TOKEN_SENTINEL;
  const existing = readBypassKey();
  if (existing) return existing;
  return ADMIN_AUTH_TOKEN_SENTINEL;
}
